import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { identifyUser, captureSignupCompleted, resetAnalytics } from "@/lib/analytics";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      // Re-identify returning sessions so PostHog links subsequent events correctly
      identifyUser(r.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    localStorage.setItem("loom_token", r.data.token);
    setUser(r.data.user);
    identifyUser(r.data.user);
    return r.data.user;
  };

  const register = async (email, password, name) => {
    const referral = localStorage.getItem("loom_ref") || undefined;
    const r = await api.post("/auth/register", { email, password, name, referral });
    localStorage.setItem("loom_token", r.data.token);
    localStorage.removeItem("loom_ref");
    setUser(r.data.user);
    captureSignupCompleted(r.data.user);
    return r.data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("loom_token");
    document.cookie = "session_token=; Max-Age=0; path=/; SameSite=None; Secure";
    resetAnalytics();
    setUser(null);
  };

  const setAuthUser = (u) => setUser(u);

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, checkAuth, setAuthUser, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
